import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { FaCheckCircle } from 'react-icons/fa'
import { IoMdNotificationsOutline } from 'react-icons/io'
import { MdClose, MdExpandLess, MdExpandMore, MdRefresh } from 'react-icons/md'

import { NotificationsModalContent, NotificationsModalStyles } from './styles'
import { formatNotificationDate, formatNotificationDay } from './utils'
import { NotificationsFeed } from './useNotifications'

interface IProps {
  feed: NotificationsFeed
  /** Notificação que o usuário clicou no painel: o modal abre com ela já expandida. */
  focusedId: number | null
  onHide: () => void
}

interface NotificationsGroup {
  day: string
  items: Notification[]
}

/** Lista completa das notificações, com o texto integral de cada uma. */
export const NotificationsModal: React.FC<IProps> = ({
  feed,
  focusedId,
  onHide
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(focusedId)

  const focusedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    focusedRef.current?.scrollIntoView({ block: 'nearest' })
  }, [])

  const groups = useMemo(
    () =>
      feed.items.reduce<NotificationsGroup[]>((accumulator, notification) => {
        const day = formatNotificationDay(notification.created_at)
        const current = accumulator[accumulator.length - 1]

        if (current && current.day === day) {
          current.items.push(notification)
          return accumulator
        }

        return [...accumulator, { day, items: [notification] }]
      }, []),
    [feed.items]
  )

  const unread = feed.items.filter(
    notification => !feed.isRead(notification)
  ).length

  /* Abrir é ler: a notificação que o usuário expandiu sai de "não lida" sem exigir o segundo
     clique no check — que continua existindo para quem quer dispensar sem abrir. */
  function toggle(id: number) {
    if (expandedId === id) {
      setExpandedId(null)
      return
    }

    setExpandedId(id)
    feed.markAsRead(id)
  }

  return (
    <>
      <NotificationsModalStyles />

      <Modal
        show
        centered
        onHide={onHide}
        dialogClassName="notifications-modal"
      >
        <NotificationsModalContent>
          <div className="notifications_modal__header">
            <IoMdNotificationsOutline className="notifications_modal__header_icon" />
            <h5>Notificações</h5>
            {unread > 0 && (
              <span className="notifications_modal__pill">
                {unread} não {unread === 1 ? 'lida' : 'lidas'}
              </span>
            )}
            <button type="button" aria-label="Fechar" onClick={onHide}>
              <MdClose />
            </button>
          </div>

          <div className="notifications_modal__list">
            {feed.loading &&
              [0, 1, 2, 3, 4].map(row => (
                <div className="notifications_modal__skeleton" key={row}>
                  <div
                    className="bg-load"
                    style={{ height: '0.95rem', width: '45%' }}
                  />
                  <div className="bg-load" style={{ height: '0.85rem' }} />
                </div>
              ))}

            {!feed.loading && feed.items.length === 0 && (
              <div className="notifications_modal__empty">
                <IoMdNotificationsOutline />
                <strong>Nenhuma notificação</strong>
                <span>
                  Aprovações, indeferimentos e avisos de operação aparecem aqui.
                </span>
              </div>
            )}

            {groups.map(group => (
              <React.Fragment key={group.day}>
                <div className="notifications_modal__day">{group.day}</div>

                {group.items.map(notification => {
                  const read = feed.isRead(notification)
                  const expanded = expandedId === notification.id

                  return (
                    <div
                      className={`notifications_modal__item ${
                        read ? '' : 'is-unread'
                      } ${expanded ? 'is-expanded' : ''}`}
                      key={notification.id}
                      ref={
                        notification.id === focusedId ? focusedRef : undefined
                      }
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <button
                          type="button"
                          className="notifications_modal__summary"
                          aria-expanded={expanded}
                          onClick={() => toggle(notification.id)}
                        >
                          <span
                            className={`notifications_modal__dot ${
                              read ? 'is-hidden' : ''
                            }`}
                          />

                          <span className="notifications_modal__body">
                            <strong>{notification.title}</strong>

                            {/* <span>, não <p>: o resumo inteiro mora dentro de um <button>,
                                que só aceita conteúdo em linha. */}
                            {!expanded && (
                              <span className="notifications_modal__preview">
                                {notification.description}
                              </span>
                            )}

                            <time dateTime={notification.created_at}>
                              {formatNotificationDate(notification.created_at)}
                            </time>
                          </span>

                          {expanded ? (
                            <MdExpandLess className="notifications_modal__chevron" />
                          ) : (
                            <MdExpandMore className="notifications_modal__chevron" />
                          )}
                        </button>

                        {expanded && (
                          <div className="notifications_modal__detail">
                            <p>{notification.description}</p>
                          </div>
                        )}
                      </div>

                      <div className="notifications_modal__actions">
                        {!read && (
                          <button
                            type="button"
                            title="Marcar como lida"
                            aria-label="Marcar como lida"
                            onClick={() => feed.markAsRead(notification.id)}
                          >
                            <FaCheckCircle />
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </React.Fragment>
            ))}
          </div>

          {!feed.loading && feed.items.length > 0 && (
            <div className="notifications_modal__footer">
              <small>
                Mostrando {feed.items.length} de {feed.total}
              </small>

              {feed.hasMore && (
                <button
                  type="button"
                  onClick={feed.loadMore}
                  disabled={feed.loadingMore}
                >
                  {feed.loadingMore ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <MdRefresh />
                  )}
                  Carregar mais
                </button>
              )}
            </div>
          )}
        </NotificationsModalContent>
      </Modal>
    </>
  )
}
